import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi, BACKEND_URL } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowUp, ArrowDown, Plus, Pencil, Trash2, Image as ImageIcon, X, Layers, RotateCcw, Search,
} from 'lucide-react';

const emptyForm = {
  topic_id: '',
  category: '',
  subcategory: '',
  title: '',
  short_description: '',
  architecture_image: null,
};

// The Add/Edit dialog only covers the card-level fields (slug, title, category,
// subcategory, short description, thumbnail) — mirroring the Featured Articles editor. The
// richer content sections (What It Is, Where It's Used, Concept, Architecture, Step-by-Step,
// Failure Signs, Related Article) still live elsewhere — the full legacy editor page or
// Content Library — reached via the "Edit Full Content" link below — new topics start as
// DRAFT so an incomplete topic is never publicly visible until an admin explicitly
// Publishes it. Topics backed by a generic page_contents doc (source "generic") save
// through updateHowItWorksTopicCard instead of updateHowItWorksTopic, and have no image
// field since page_contents has none — see HowItWorksCardUpdate on the backend.
const AdminHowItWorksPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingSource, setEditingSource] = useState('legacy');
  const [editingSlug, setEditingSlug] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reorderingId, setReorderingId] = useState(null);
  const [activeTab, setActiveTab] = useState('active');
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  const [hardDeleting, setHardDeleting] = useState(false);
  const [restoringKey, setRestoringKey] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getHowItWorksTopicsAdmin();
      setItems(data);
    } catch (err) {
      console.error('Error fetching how-it-works topics:', err);
      toast.error('Failed to load topics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openCreateDialog = () => {
    setEditingId(null);
    setEditingSource('legacy');
    setEditingSlug(null);
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingId(item.topic_id);
    setEditingSource(item.source || 'legacy');
    setEditingSlug(item.slug || null);
    setFormData({
      topic_id: item.topic_id,
      category: item.category || '',
      subcategory: item.subcategory || '',
      title: item.title,
      short_description: item.short_description || '',
      architecture_image: item.architecture_image || null,
    });
    setDialogOpen(true);
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const result = await adminApi.uploadImage(file, 'how-it-works');
      setFormData((prev) => ({ ...prev, architecture_image: result.path }));
    } catch (err) {
      console.error('Error uploading image:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, architecture_image: null }));
  };

  const validateForm = () => {
    if (!formData.topic_id.trim()) {
      toast.error('Slug is required');
      return false;
    }
    if (!/^[a-z0-9-]+$/.test(formData.topic_id.trim())) {
      toast.error('Slug must contain only lowercase letters, numbers, and hyphens');
      return false;
    }
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return false;
    }
    if (!formData.short_description.trim()) {
      toast.error('Short description is required');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (editingId) {
        if (editingSource === 'generic') {
          const updated = await adminApi.updateHowItWorksTopicCard(editingId, {
            title: formData.title.trim(),
            description: formData.short_description.trim(),
            category: formData.category.trim() || null,
            subcategory: formData.subcategory.trim() || null,
          });
          setItems((prev) => prev.map((it) => (
            it.topic_id === editingId && it.source === 'generic' ? { ...it, ...updated } : it
          )));
        } else {
          const updated = await adminApi.updateHowItWorksTopic(editingId, {
            category: formData.category.trim() || null,
            subcategory: formData.subcategory.trim() || null,
            title: formData.title.trim(),
            short_description: formData.short_description.trim(),
            architecture_image: formData.architecture_image || null,
          });
          setItems((prev) => prev.map((it) => (
            it.topic_id === editingId && it.source !== 'generic' ? { ...it, ...updated } : it
          )));
        }
        toast.success('Topic updated');
        setDialogOpen(false);
      } else {
        const created = await adminApi.createHowItWorksTopic({
          topic_id: formData.topic_id.trim(),
          category: formData.category.trim() || null,
          title: formData.title.trim(),
          short_description: formData.short_description.trim(),
          architecture_image: formData.architecture_image || null,
          what_it_is: '',
          where_its_used: '',
          concept: '',
          architecture_components: [],
          step_by_step_steps: [],
          failure_signs: [],
        });
        toast.success('Topic created as a draft — add the rest of its content below');
        setDialogOpen(false);
        navigate(`/admin/how-it-works/${created.topic_id}`);
      }
    } catch (err) {
      console.error('Error saving how-it-works topic:', err);
      toast.error(err.response?.data?.detail || 'Failed to save topic');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.source === 'generic') {
        await adminApi.deleteHowItWorksPageContent(deleteTarget.slug);
      } else {
        await adminApi.deleteHowItWorksTopic(deleteTarget.topic_id);
      }
      setItems((prev) => prev.map((it) => (
        it.topic_id === deleteTarget.topic_id && it.source === deleteTarget.source
          ? { ...it, is_deleted: true }
          : it
      )));
      toast.success('Topic moved to Deleted');
    } catch (err) {
      console.error('Error deleting how-it-works topic:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete topic');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleRestore = async (item) => {
    const key = `${item.source}-${item.topic_id}`;
    setRestoringKey(key);
    try {
      if (item.source === 'generic') {
        await adminApi.restoreHowItWorksPageContent(item.slug);
      } else {
        await adminApi.restoreHowItWorksTopic(item.topic_id);
      }
      setItems((prev) => prev.map((it) => (
        it.topic_id === item.topic_id && it.source === item.source ? { ...it, is_deleted: false } : it
      )));
      toast.success('Topic restored');
    } catch (err) {
      console.error('Error restoring how-it-works topic:', err);
      toast.error(err.response?.data?.detail || 'Failed to restore topic');
    } finally {
      setRestoringKey(null);
    }
  };

  const handleHardDelete = async () => {
    if (!hardDeleteTarget) return;
    setHardDeleting(true);
    try {
      if (hardDeleteTarget.source === 'generic') {
        await adminApi.hardDeleteHowItWorksPageContent(hardDeleteTarget.slug);
      } else {
        await adminApi.hardDeleteHowItWorksTopic(hardDeleteTarget.topic_id);
      }
      setItems((prev) => prev.filter(
        (it) => !(it.topic_id === hardDeleteTarget.topic_id && it.source === hardDeleteTarget.source)
      ));
      toast.success('Topic permanently deleted');
    } catch (err) {
      console.error('Error permanently deleting how-it-works topic:', err);
      toast.error(err.response?.data?.detail || 'Failed to permanently delete topic');
    } finally {
      setHardDeleting(false);
      setHardDeleteTarget(null);
    }
  };

  const activeItems = items.filter((it) => !it.is_deleted);
  const deletedItems = items.filter((it) => it.is_deleted);

  const matchesSearch = (it) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return [it.title, it.short_description, it.category, it.subcategory, it.topic_id]
      .filter(Boolean)
      .some((field) => field.toLowerCase().includes(q));
  };

  // Reordering keeps operating on the full, unfiltered activeItems array (see
  // handleMove below) — the move buttons are disabled whenever a search is active
  // so a filtered index is never passed to it.
  const displayedActiveItems = activeItems.filter(matchesSearch);
  const displayedDeletedItems = deletedItems.filter(matchesSearch);
  const isSearching = searchQuery.trim() !== '';

  const handleMove = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= activeItems.length) return;
    if (activeItems[index].source === 'generic' || activeItems[newIndex].source === 'generic') return;

    const reorderedActive = [...activeItems];
    [reorderedActive[index], reorderedActive[newIndex]] = [reorderedActive[newIndex], reorderedActive[index]];
    setItems([...reorderedActive, ...deletedItems]);

    const movedId = reorderedActive[newIndex].topic_id;
    setReorderingId(movedId);
    try {
      const legacyOnly = reorderedActive.filter((it) => it.source !== 'generic');
      await adminApi.reorderHowItWorksTopics(
        legacyOnly.map((it, i) => ({ topic_id: it.topic_id, order: i }))
      );
    } catch (err) {
      console.error('Error reordering how-it-works topics:', err);
      toast.error('Failed to save new order');
      fetchItems();
    } finally {
      setReorderingId(null);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] py-8 px-4" data-testid="admin-how-it-works-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="ghost" size="icon" data-testid="back-to-admin-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="font-chivo text-2xl font-black uppercase tracking-tight">How It Works Topics</h1>
            <p className="text-muted-foreground text-sm">
              Topics shown on the "How Does It Work?" page
            </p>
          </div>
          <Button
            onClick={openCreateDialog}
            className="font-bold uppercase tracking-wider gap-2"
            data-testid="add-how-it-works-topic-btn"
          >
            <Plus className="h-4 w-4" />
            Add Topic
          </Button>
        </div>

        {!loading && (
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by title, category, or slug…"
              className="pl-9"
              data-testid="hiw-search-input"
            />
          </div>
        )}

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="border-2">
                <CardContent className="p-6">
                  <Skeleton className="h-4 w-20 mb-3" />
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6 bg-white border-2">
              <TabsTrigger value="active" className="font-bold uppercase tracking-wider" data-testid="hiw-tab-active">
                Active ({activeItems.length})
              </TabsTrigger>
              <TabsTrigger value="deleted" className="font-bold uppercase tracking-wider" data-testid="hiw-tab-deleted">
                Deleted ({deletedItems.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="active">
              {displayedActiveItems.length === 0 ? (
                <Card className="border-2 border-dashed">
                  <CardContent className="py-12 text-center">
                    <Layers className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      {isSearching ? 'No topics match your search.' : 'No How It Works topics yet.'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {displayedActiveItems.map((item, index) => (
                    <Card
                      key={`${item.source || 'legacy'}-${item.topic_id}`}
                      className="border-2 technical-card"
                      data-testid={`how-it-works-row-${item.topic_id}`}
                    >
                      <CardContent className="p-4 flex items-start gap-4">
                        <div className="flex flex-col gap-1 pt-1">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            disabled={isSearching || item.source === 'generic' || index === 0 || reorderingId === item.topic_id}
                            onClick={() => handleMove(index, 'up')}
                            aria-label="Move up"
                            data-testid={`move-up-${item.topic_id}`}
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            disabled={isSearching || item.source === 'generic' || index === displayedActiveItems.length - 1 || reorderingId === item.topic_id}
                            onClick={() => handleMove(index, 'down')}
                            aria-label="Move down"
                            data-testid={`move-down-${item.topic_id}`}
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </Button>
                        </div>

                        {item.architecture_image ? (
                          <img
                            src={`${BACKEND_URL}${item.architecture_image}`}
                            alt=""
                            className="h-20 w-28 object-cover rounded-sm border border-structure shrink-0"
                          />
                        ) : (
                          <div className="h-20 w-28 rounded-sm border border-dashed border-structure flex items-center justify-center shrink-0 text-muted-foreground">
                            <ImageIcon className="h-6 w-6" />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {item.category && (
                              <span className="font-mono text-xs uppercase tracking-wider text-autoxium-blue">
                                {item.category}
                              </span>
                            )}
                            <Badge
                              variant="outline"
                              className={`font-mono text-[10px] uppercase tracking-wider ${
                                item.status === 'PUBLISHED' ? 'border-emerald-500/50 text-emerald-700' : 'border-amber-500/50 text-amber-700'
                              }`}
                            >
                              {item.status}
                            </Badge>
                          </div>
                          <h3 className="font-chivo text-base font-bold mb-1 truncate">{item.title}</h3>
                          <p className="text-muted-foreground text-sm line-clamp-2 mb-1">{item.short_description}</p>
                          <p className="text-xs text-muted-foreground font-mono truncate">/how-it-works/{item.topic_id}</p>
                        </div>

                        <div className="flex flex-col gap-2 shrink-0">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => openEditDialog(item)}
                            aria-label="Edit"
                            data-testid={`edit-how-it-works-${item.topic_id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeleteTarget(item)}
                            aria-label="Delete"
                            data-testid={`delete-how-it-works-${item.topic_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="deleted">
              {displayedDeletedItems.length === 0 ? (
                <Card className="border-2 border-dashed">
                  <CardContent className="py-12 text-center">
                    <Trash2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      {isSearching ? 'No deleted topics match your search.' : 'No deleted topics.'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {displayedDeletedItems.map((item) => (
                    <Card
                      key={`${item.source || 'legacy'}-${item.topic_id}`}
                      className="border-2 technical-card"
                      data-testid={`how-it-works-deleted-row-${item.topic_id}`}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {item.category && (
                              <span className="font-mono text-xs uppercase tracking-wider text-autoxium-blue">
                                {item.category}
                              </span>
                            )}
                            <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-wider">
                              {item.status}
                            </Badge>
                          </div>
                          <h3 className="font-chivo text-base font-bold mb-1 truncate">{item.title}</h3>
                          <p className="text-muted-foreground text-sm line-clamp-2">{item.short_description}</p>
                        </div>

                        <div className="flex flex-col gap-2 shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={restoringKey === `${item.source}-${item.topic_id}`}
                            onClick={() => handleRestore(item)}
                            data-testid={`restore-how-it-works-${item.topic_id}`}
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            {restoringKey === `${item.source}-${item.topic_id}` ? 'Restoring...' : 'Restore'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-destructive text-destructive hover:bg-destructive hover:text-white"
                            onClick={() => setHardDeleteTarget(item)}
                            data-testid={`hard-delete-how-it-works-${item.topic_id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Permanently
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-chivo uppercase">
              {editingId ? 'Edit How It Works Topic' : 'Add How It Works Topic'}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? 'Updates the card shown on the "How Does It Work?" page.'
                : 'Creates a draft topic — use "Edit Full Content" afterward to fill in the rest and Publish it.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="hiw-slug">Slug *</Label>
              <Input
                id="hiw-slug"
                placeholder="hvil"
                value={formData.topic_id}
                disabled={!!editingId}
                onChange={(e) => handleFieldChange('topic_id', e.target.value)}
                data-testid="hiw-slug-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Lowercase letters, numbers, hyphens only. Can't be changed after creation.
              </p>
            </div>

            <div>
              <Label htmlFor="hiw-link-path">Link Path</Label>
              <Input
                id="hiw-link-path"
                value={`/how-it-works/${formData.topic_id || '...'}`}
                disabled
                data-testid="hiw-link-path-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Derived from the slug — not separately editable.
              </p>
            </div>

            <div>
              <Label htmlFor="hiw-title">Title *</Label>
              <Input
                id="hiw-title"
                value={formData.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                data-testid="hiw-title-input"
              />
            </div>

            <div>
              <Label htmlFor="hiw-category">Category</Label>
              <Input
                id="hiw-category"
                placeholder="EV Safety Systems"
                value={formData.category}
                onChange={(e) => handleFieldChange('category', e.target.value)}
                data-testid="hiw-category-input"
              />
            </div>

            <div>
              <Label htmlFor="hiw-subcategory">Subcategory</Label>
              <Input
                id="hiw-subcategory"
                placeholder="e.g. BMS"
                value={formData.subcategory}
                onChange={(e) => handleFieldChange('subcategory', e.target.value)}
                data-testid="hiw-subcategory-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Groups topics under the category header on the public page.
              </p>
            </div>

            <div>
              <Label htmlFor="hiw-short-description">Short Description *</Label>
              <Textarea
                id="hiw-short-description"
                rows={2}
                value={formData.short_description}
                onChange={(e) => handleFieldChange('short_description', e.target.value)}
                data-testid="hiw-short-description-input"
              />
            </div>

            {editingSource === 'generic' ? (
              <p className="text-xs text-muted-foreground">
                This topic's content is managed in Content Library, which has no card image field.
              </p>
            ) : (
              <div>
                <Label>Image</Label>
                {formData.architecture_image ? (
                  <div className="relative w-fit mt-2">
                    <img
                      src={`${BACKEND_URL}${formData.architecture_image}`}
                      alt=""
                      className="h-28 w-40 object-cover rounded-sm border border-structure"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-1"
                      aria-label="Remove image"
                      data-testid="hiw-remove-image-btn"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div className="mt-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      id="hiw-image-input"
                      data-testid="hiw-image-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={uploadingImage}
                      onClick={() => fileInputRef.current?.click()}
                      className="gap-2"
                    >
                      <ImageIcon className="h-4 w-4" />
                      {uploadingImage ? 'Uploading…' : 'Upload Image'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="sm:justify-between">
            {editingId ? (
              <Link
                to={editingSource === 'generic' ? `/admin/content/${editingSlug}` : `/admin/how-it-works/${editingId}`}
                className="text-sm text-autoxium-blue hover:underline self-center"
                data-testid="hiw-edit-full-content-link"
              >
                Edit Full Content →
              </Link>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="hiw-save-btn">
                {saving ? 'Saving…' : editingId ? 'Save' : 'Create & Continue'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-chivo uppercase">Delete How It Works Topic?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes "{deleteTarget?.title}" from the "How Does It Work?" page and moves it to the
              Deleted tab, where it can be restored later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-how-it-works-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hard delete confirmation */}
      <AlertDialog open={!!hardDeleteTarget} onOpenChange={() => setHardDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-chivo uppercase">Permanently Delete Topic?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete "{hardDeleteTarget?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleHardDelete}
              disabled={hardDeleting}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-hard-delete-how-it-works-btn"
            >
              {hardDeleting ? 'Deleting...' : 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminHowItWorksPage;
